import axios from 'axios';
import get from 'lodash/get';

import {
  SYNCFY_SESSION_TOKEN,
  SYNCFY_ACCOUNTS,
  SYNCFY_SITE_ORGANIZATIONS,
  PAYMENT_RECONCILIATION,
  PAYMENT_RECONCILIATION_INVOICES,
  PAYMENT_RECONCILIATION_RECONCILE
} from '../../../constants/endpoints';

export const GET_SYNCFY_SESSION_TOKEN = 'GET_SYNCFY_SESSION_TOKEN';
export const GET_SYNCFY_SESSION_TOKEN_LOADING = 'GET_SYNCFY_SESSION_TOKEN_LOADING';

export const GET_SYNCFY_ACCOUNTS = 'GET_SYNCFY_ACCOUNTS';
export const GET_SYNCFY_ACCOUNTS_LOADING = 'GET_SYNCFY_ACCOUNTS_LOADING';

export const GET_SYNCFY_SITE_ORGANIZATIONS = 'GET_SYNCFY_SITE_ORGANIZATIONS';

export const GET_PAYMENT_RECONCILIATION = 'GET_PAYMENT_RECONCILIATION';
export const GET_PAYMENT_RECONCILIATION_LOADING = 'GET_PAYMENT_RECONCILIATION_LOADING';

export const GET_PAYMENT_RECONCILIATION_INVOICES = 'GET_PAYMENT_RECONCILIATION_INVOICES';
export const GET_PAYMENT_RECONCILIATION_INVOICES_LOADING = 'GET_PAYMENT_RECONCILIATION_INVOICES_LOADING';

export const SELECT_TRANSACTION_FOR_RECONCILIATION = 'SELECT_TRANSACTION_FOR_RECONCILIATION';
export const SELECT_INVOICE_FOR_RECONCILIATION = 'SELECT_INVOICE_FOR_RECONCILIATION';
export const DESELECT_INVOICE_FOR_RECONCILIATION = 'DESELECT_INVOICE_FOR_RECONCILIATION';
export const UPDATE_INVOICE_AMOUNT_TO_APPLY = 'UPDATE_INVOICE_AMOUNT_TO_APPLY';
export const CLEAR_RECONCILIATION_SELECTION = 'CLEAR_RECONCILIATION_SELECTION';

export const RECONCILE_PAYMENT = 'RECONCILE_PAYMENT';
export const RECONCILE_PAYMENT_LOADING = 'RECONCILE_PAYMENT_LOADING';

export const getSyncfySessionTokenAction = () => {
  return async (dispatch, getState) => {
    dispatch({ type: GET_SYNCFY_SESSION_TOKEN_LOADING });
    const currentToken = get(getState(), 'bankReconciliation.syncfySessionToken', null);
    const response = await axios.post(SYNCFY_SESSION_TOKEN, { token: currentToken });
    const newToken = get(response, 'data.data.token', null);
    dispatch({ type: GET_SYNCFY_SESSION_TOKEN, payload: newToken });
  };
};

export const getSyncfyAccountsAction = (setLoading = true) => {
  return async (dispatch, getState) => {
    if (setLoading) dispatch({ type: GET_SYNCFY_ACCOUNTS_LOADING });
    const sessionToken = get(getState(), 'bankReconciliation.syncfySessionToken', null);
    const response = await axios.post(SYNCFY_ACCOUNTS, { sessionToken });
    const accounts = get(response, 'data.data', []);
    dispatch({ type: GET_SYNCFY_ACCOUNTS, payload: accounts });
  };
};

export const getSyncfySiteOrganizationsAction = () => {
  return async dispatch => {
    try {
      const response = await axios.get(SYNCFY_SITE_ORGANIZATIONS);
      const organizations = get(response, 'data.data', []);
      dispatch({
        type: GET_SYNCFY_SITE_ORGANIZATIONS,
        payload: Array.isArray(organizations) ? organizations : []
      });
    } catch (error) {
      console.error('Error getting site organizations:', error);
    }
  };
};

/**
 * Get payment reconciliation transactions with optional filters
 * @param {Object} filters - Optional filters
 * @param {number} filters.date - Unix timestamp for date filter
 * @param {number} filters.account_id - Account ID filter
 * @param {number} filters.amount - Specific amount filter
 * @param {string} filters.description - Description filter
 * @param {string} filters.sort - Sort parameter (e.g., 'amount-ASC', 'date_transaction-DESC')
 * @param {string} filters.status - Status filter ('all', 'reconciled', 'not_reconciled')
 */
export const getPaymentReconciliationAction = (filters = {}) => {
  return async dispatch => {
    try {
      const isFirstPage = !filters.page || filters.page === 1;
      dispatch({ 
        type: GET_PAYMENT_RECONCILIATION_LOADING,
        payload: { clearTransactions: isFirstPage }
      });

      const params = new URLSearchParams();

      if (filters.page) params.append('page', filters.page);
      if (filters.date) params.append('date', filters.date);
      if (filters.account_id) params.append('account_id', filters.account_id);
      if (filters.amount) params.append('amount', filters.amount);
      if (filters.description) params.append('description', filters.description);
      if (filters.sort) params.append('sort', filters.sort);
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }

      const queryString = params.toString();
      const url = queryString ? `${PAYMENT_RECONCILIATION}?${queryString}` : PAYMENT_RECONCILIATION;

      const response = await axios.get(url);
      const transactions = get(response, 'data.data.data', []);
      const pagination = get(response, 'data.data.pagination', {});
      dispatch({
        type: GET_PAYMENT_RECONCILIATION,
        payload: { transactions, pagination }
      });
    } catch (error) {
      console.error('Error getting payment reconciliation:', error);
    }
  };
};

/**
 * Get payment reconciliation invoices with optional filters
 * @param {Object} filters - Optional filters
 * @param {string} filters.general - General search filter (folio or cliente)
 * @param {number} filters.issue_date - Unix timestamp for issue date filter
 * @param {number} filters.amount - Amount filter
 * @param {string} filters.status - Status filter (e.g., 'pending')
 * @param {string} filters.sort - Sort parameter (e.g., 'balance-DESC', 'due_date-ASC')
 * @param {number} filters.page - Page number
 * @param {number} filters.limit - Items per page
 */
export const getPaymentReconciliationInvoicesAction = (filters = {}) => {
  return async dispatch => {
    try {
      const isFirstPage = !filters.page || filters.page === 1;
      dispatch({ 
        type: GET_PAYMENT_RECONCILIATION_INVOICES_LOADING,
        payload: { clearInvoices: isFirstPage }
      });

      const params = new URLSearchParams();

      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.general) params.append('general', filters.general);
      if (filters.issue_date) params.append('issue_date', filters.issue_date);
      if (filters.status) params.append('status', filters.status);
      if (filters.amount) params.append('amount', filters.amount);
      if (filters.sort) params.append('sort', filters.sort);

      const queryString = params.toString();
      const url = queryString ? `${PAYMENT_RECONCILIATION_INVOICES}?${queryString}` : PAYMENT_RECONCILIATION_INVOICES;

      const response = await axios.get(url);
      const invoices = get(response, 'data.data.data', []);
      const pagination = get(response, 'data.data.pagination', {});

      dispatch({
        type: GET_PAYMENT_RECONCILIATION_INVOICES,
        payload: {
          invoices: Array.isArray(invoices) ? invoices : [],
          pagination
        }
      });
    } catch (error) {
      console.error('Error getting payment reconciliation invoices:', error);
    }
  };
};

export const reconcilePaymentAction = reconciliationBody => {
  return async dispatch => {
    try {
      dispatch({ type: RECONCILE_PAYMENT_LOADING });

      const response = await axios.post(PAYMENT_RECONCILIATION_RECONCILE, reconciliationBody);
      const data = get(response, 'data.data.data', {});

      dispatch({
        type: RECONCILE_PAYMENT,
        payload: data
      });

      return { success: true, data };
    } catch (error) {
      console.error('Error reconciling payment:', error);
      return { success: false, error };
    }
  };
};

export const selectTransactionForReconciliation = transaction => ({
  type: SELECT_TRANSACTION_FOR_RECONCILIATION,
  payload: transaction
});

export const selectInvoiceForReconciliation = invoice => ({
  type: SELECT_INVOICE_FOR_RECONCILIATION,
  payload: invoice
});

export const deselectInvoiceForReconciliation = invoiceUuid => ({
  type: DESELECT_INVOICE_FOR_RECONCILIATION,
  payload: invoiceUuid
});

export const updateInvoiceAmountToApply = (invoiceUuid, amount) => ({
  type: UPDATE_INVOICE_AMOUNT_TO_APPLY,
  payload: { invoiceUuid, amount }
});

export const clearReconciliationSelection = () => ({
  type: CLEAR_RECONCILIATION_SELECTION
});



// WEBPACK FOOTER //
// ./src/containers/bank-reconciliation/store/actions.js